import React, { useRef, useEffect, useCallback, useState } from 'react';
import { FaClock, FaCopy, FaTrash, FaQuestionCircle, FaCloud, FaCog } from "react-icons/fa";
import { MdFeedback } from 'react-icons/md';
import './SuppliersDetailPage.css';

// Define getCurrentDate function - UPDATED TO DD-MM-YYYY
const getCurrentDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
};

// Add this function to convert DD-MM-YYYY to YYYY-MM-DD for date input
const formatDateForInput = (dateString) => {
    if (!dateString) return new Date().toISOString().split('T')[0];

    if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2) {
            // Convert DD-MM-YYYY to YYYY-MM-DD for input type="date"
            const day = parts[0];
            const month = parts[1];
            const year = parts[2];
            return `${year}-${month}-${day}`;
        }
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return new Date().toISOString().split('T')[0];

    return date.toISOString().split('T')[0];
};

const SupplierExpensePopup = ({
    showCreateExpensePopup = false,
    setShowCreateExpensePopup = () => { },
    supplierData = {},
    paymentAccount = "",
    setPaymentAccount = () => { },
    paymentDate = "",
    setPaymentDate = () => { },
    paymentMethod = "",
    setPaymentMethod = () => { },
    description = "",
    setDescription = () => { },
    category = "",
    setCategory = () => { },
    expenseFile = null,
    setExpenseFile = () => { },
    tableData = [],
    setTableData = () => { },
    totalAmount = 0,
    handleExpenseCreated = () => { },
    handleSaveAndNew = () => { },
    handleClear = () => { },
    handlePrint = () => { },
    handleRecurring = () => { },
    handleAddRow = () => { },
    handleDeleteRow = () => { },
    handleCopyRow = () => { },
    handleUpdateRow = () => { },
    handleClearAllLines = () => { },
    handleFileChange = () => { },
    selectedExpense = null,
}) => {
    const createPopupRef = useRef(null);
    const fileInputRef = useRef(null);
    const payeeRef = useRef(null);
    const [categories, setCategories] = useState([]);
    const [categoryMap, setCategoryMap] = useState({});
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [error, setError] = useState(null);
    const [expenseNumber, setExpenseNumber] = useState('');
    const API_BASE_URL = "https://backend.hira-tech.co.uk";

    // Fetch categories from backend
    useEffect(() => {
        setLoadingCategories(true);
        fetch(`${API_BASE_URL}/api/categories/`, {
            headers: { Accept: "application/json" },
        })
            .then((response) => {
                if (!response.ok) throw new Error(`Categories fetch error! Status: ${response.status}`);
                return response.json();
            })
            .then((data) => {
                setCategories(data);
                const map = data.reduce((acc, cat) => {
                    acc[cat.name] = cat.display_name;
                    return acc;
                }, {});
                setCategoryMap(map);
                setError(null);
            })
            .catch((error) => {
                console.error(`Failed to fetch categories: ${error.message}`);
                setError(`Failed to fetch categories: ${error.message}`);
            })
            .finally(() => setLoadingCategories(false));
    }, []);

    // Handle outside click to close popup
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (createPopupRef.current && !createPopupRef.current.contains(event.target)) {
                setShowCreateExpensePopup(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [setShowCreateExpensePopup]);

    // Initialize form when selectedExpense changes
    useEffect(() => {
        const mapBackendCategory = (backendCategory) => {
            return categoryMap[backendCategory] ? backendCategory : "General";
        };

        if (selectedExpense && selectedExpense.items) {
            const mappedCategory = mapBackendCategory(selectedExpense.terms);
            setTableData(
                selectedExpense.items.map((item, index) => ({
                    id: item.id || Date.now() + index,
                    rowNumber: item.rowNumber || (index + 1).toString(),
                    category: mapBackendCategory(item.category),
                    description: item.description || "",
                    totalAmount: item.totalAmount || "", // Changed from 0.01 to empty string
                }))
            );
            setPaymentAccount(selectedExpense.paymentAccount || "");
            // UPDATED: Set payment date as is (DD-MM-YYYY format)
            setPaymentDate(selectedExpense.date || getCurrentDate());
            setPaymentMethod(selectedExpense.method || "");
            setDescription(selectedExpense.memo || "");
            setCategory(mappedCategory);
            setExpenseFile(
                selectedExpense.file_path && selectedExpense.file_path !== ""
                    ? { name: selectedExpense.file_path.split('/').pop() }
                    : null
            );
            // Set expense number from selected expense - NO AUTO GENERATION
            setExpenseNumber(selectedExpense.expense_number || '');
        } else {
            setTableData([
                {
                    id: Date.now(),
                    rowNumber: "1",
                    category: "General",
                    description: "",
                    totalAmount: "", // Changed from "0.01" to empty string
                },
            ]);
            setPaymentAccount("");
            setPaymentMethod("");
            // UPDATED: Set current date in DD-MM-YYYY format
            setPaymentDate(getCurrentDate());
            setDescription("");
            setCategory("General");
            setExpenseFile(null);
            // ✅ FIXED: Set empty expense number for new expenses - NO AUTO GENERATION
            setExpenseNumber('');
        }
    }, [selectedExpense, setTableData, setPaymentAccount, setPaymentDate, setPaymentMethod, setDescription, setCategory, setExpenseFile, categoryMap]);

    // Recalculate totalAmount when tableData changes
    useEffect(() => {
        const calculatedTotal = Array.isArray(tableData)
            ? tableData.reduce((sum, item) => {
                const amount = parseFloat(item?.totalAmount) || 0;
                return sum + amount;
            }, 0).toFixed(2)
            : "0.00";
        // Update totalAmount prop if needed
    }, [tableData]);

    // ADD THIS FUNCTION: Handle date change for the input field
    const handleDateChange = (e) => {
        const inputDate = e.target.value; // This will be in YYYY-MM-DD from input type="date"
        if (inputDate) {
            const [year, month, day] = inputDate.split('-');
            // Convert to DD-MM-YYYY format
            const formattedDate = `${day}-${month}-${year}`;
            setPaymentDate(formattedDate);
        } else {
            setPaymentDate(getCurrentDate());
        }
    };

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!supplierData?.id) {
                alert("Supplier ID is missing. Please select a valid supplier.");
                return;
            }
            if (!expenseNumber.trim()) {
                alert("Please enter a reference number.");
                return;
            }
            if (!Array.isArray(tableData) || tableData.length === 0) {
                setTableData([{ id: Date.now(), rowNumber: "1", category: "General", description: "", totalAmount: "" }]);
                return;
            }
            const validTableData = tableData.map((item) => ({
                rowNumber: item.rowNumber || "1",
                category: item.category || "General",
                description: item.description || "",
                totalAmount: parseFloat(item.totalAmount || 0), // Handle empty input as 0
            }));
            if (validTableData.some((item) => isNaN(item.totalAmount) || item.totalAmount < 0)) {
                alert("All item amounts must be non-negative numbers.");
                return;
            }
            const total = parseFloat(validTableData.reduce((sum, item) => sum + item.totalAmount, 0).toFixed(2));
            const expenseData = {
                date: paymentDate || getCurrentDate(), // UPDATED: Now in DD-MM-YYYY format
                type: "Expense",
                expense_number: expenseNumber.trim(), // Use the editable expense number
                supplier: parseInt(supplierData.id),
                method: paymentMethod || "Cash",
                terms: category || "General",
                memo: description || "",
                amount: total.toFixed(2),
                total: total.toFixed(2),
                sales_tax: (total * 0.17 / 1.17).toFixed(2),
                file_name: expenseFile ? expenseFile.name : "",
                file_path: expenseFile ? `/Uploads/expenses/${expenseFile.name}` : "",
                items: validTableData,
                paymentAccount: paymentAccount || "Cash on hand",
                status: selectedExpense ? selectedExpense.status : "Pending",
            };
            handleExpenseCreated(expenseData, expenseFile);
        },
        [supplierData?.id, paymentMethod, paymentDate, tableData, description, expenseFile, selectedExpense, handleExpenseCreated, paymentAccount, category, expenseNumber]
    );

    const handleSaveAndNewClick = useCallback(
        () => {
            if (!supplierData?.id) {
                alert("Supplier ID is missing. Please select a valid supplier.");
                return;
            }
            if (!expenseNumber.trim()) {
                alert("Please enter a reference number.");
                return;
            }
            if (!Array.isArray(tableData) || tableData.length === 0) {
                setTableData([{ id: Date.now(), rowNumber: "1", category: "General", description: "", totalAmount: "" }]);
                return;
            }
            const validTableData = tableData.map((item) => ({
                rowNumber: item.rowNumber || "1",
                category: item.category || "General",
                description: item.description || "",
                totalAmount: parseFloat(item.totalAmount || 0), // Handle empty input as 0
            }));
            if (validTableData.some((item) => isNaN(item.totalAmount) || item.totalAmount < 0)) {
                alert("All item amounts must be non-negative numbers.");
                return;
            }
            const total = parseFloat(validTableData.reduce((sum, item) => sum + item.totalAmount, 0).toFixed(2));
            const expenseData = {
                date: paymentDate || getCurrentDate(), // UPDATED: Now in DD-MM-YYYY format
                type: "Expense",
                expense_number: expenseNumber.trim(), // Use the editable expense number
                supplier: parseInt(supplierData.id),
                method: paymentMethod || "Cash",
                terms: category || "General",
                memo: description || "",
                amount: total.toFixed(2),
                total: total.toFixed(2),
                sales_tax: (total * 0.17 / 1.17).toFixed(2),
                file_name: expenseFile ? expenseFile.name : "",
                file_path: expenseFile ? `/Uploads/expenses/${expenseFile.name}` : "",
                items: validTableData,
                paymentAccount: paymentAccount || "Cash on hand",
                status: "Pending",
            };
            handleSaveAndNew(expenseData, expenseFile);
            setPaymentDate(getCurrentDate());
            setPaymentAccount("");
            setPaymentMethod("");
            setDescription("");
            setCategory("General");
            setExpenseFile(null);
            // ✅ FIXED: Reset expense number to empty for new expense - NO AUTO GENERATION
            setExpenseNumber('');
            setTableData([
                {
                    id: Date.now(),
                    rowNumber: "1",
                    category: "General",
                    description: "",
                    totalAmount: "", // Changed from "0.01" to empty string
                },
            ]);
        },
        [supplierData?.id, paymentMethod, paymentDate, tableData, description, expenseFile, handleSaveAndNew, paymentAccount, category, setPaymentDate, setPaymentAccount, setPaymentMethod, setDescription, setCategory, setExpenseFile, setTableData, expenseNumber]
    );

    const handleClearLocal = () => {
        handleClear();
        setPaymentDate(getCurrentDate());
        setPaymentAccount("");
        setPaymentMethod("");
        setDescription("");
        setCategory("General");
        setExpenseFile(null);
        // ✅ FIXED: Reset expense number to empty - NO AUTO GENERATION
        setExpenseNumber('');
        setTableData([
            {
                id: Date.now(),
                rowNumber: "1",
                category: "General",
                description: "",
                totalAmount: "", // Changed from "0.01" to empty string
            },
        ]);
    };

    return (
        showCreateExpensePopup && (
            <div className="upload-popup-overlay">
                <div className="create-bill-popup" ref={createPopupRef} onClick={(e) => e.stopPropagation()}>
                    {error && <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>Error: {error}</div>}
                    {loadingCategories && <div>Loading categories...</div>}
                    <div className="bill-form-header">
                        <div className="header-title">
                            <FaClock className="header-icon" />
                            <span>{selectedExpense ? "Edit Expense" : "New Expense"}</span>
                        </div>
                        <div className="header-actions">
                            <button type="button" className="icon-btn" onClick={() => alert("Copy expense not implemented")}>
                                <FaCopy />
                                <span>Copy</span>
                            </button>
                            <button type="button" className="text-icon-btn">
                                <MdFeedback />
                                <span>Give Feedback</span>
                            </button>
                            <button type="button" className="icon-btn">
                                <FaQuestionCircle />
                            </button>
                            <button className="close-button" onClick={() => setShowCreateExpensePopup(false)}>×</button>
                        </div>
                    </div>
                    <h2 className="form-main-title">{selectedExpense ? "Edit Expense" : "New Expense"}</h2>
                    <form
                        onSubmit={handleSubmit}
                        className="bill-form-container"
                    >
                        <div className="form-section">
                            <div className="form-row">
                                <div className="form-group wide-group">
                                    <label>Payee <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        ref={payeeRef}
                                        type="text"
                                        value={supplierData?.name || ""}
                                        onChange={(e) => { }} // Disabled input, no-op
                                        disabled
                                        style={{ width: "300px", backgroundColor: "#f5f5f5" }}
                                    />
                                </div>
                                <div className="form-group amount-display">
                                    <label>Balance Due</label>
                                    <p style={{ fontWeight: "bold", color: "#333", fontSize: "15px" }}>
                                        £{totalAmount ? parseFloat(totalAmount).toFixed(2) : "0.00"}
                                    </p>
                                </div>
                            </div>
                            <div className="form-row five-items-row">
                                <div className="form-group">
                                    <label>Payment account</label>
                                    <select
                                        value={paymentAccount || ""}
                                        onChange={(e) => setPaymentAccount(e.target.value)}
                                        style={{ width: "200px" }}
                                    >
                                        <option value="Cash on hand">Cash on hand</option>
                                        <option value="Bank">Bank</option>
                                        <option value="Credit Card">Credit Card</option>
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-80px" }}>
                                    <label>Payment Date</label>
                                    {/* UPDATED: Use formatDateForInput and handleDateChange */}
                                    <input
                                        type="date"
                                        value={formatDateForInput(paymentDate)}
                                        onChange={handleDateChange}
                                        style={{ width: "200px" }}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-160px" }}>
                                    <label>Payment Method</label>
                                    <select
                                        value={paymentMethod || ""}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        style={{ width: "200px" }}
                                    >
                                        <option value="">Select method</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Card">Card</option>
                                        <option value="Bank Transfer">Bank Transfer</option>
                                    </select>
                                </div>
                                <div className="form-group" style={{ width: "200px", marginLeft: "-240px" }}>
                                    <label>Ref. no. <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        value={expenseNumber}
                                        onChange={(e) => setExpenseNumber(e.target.value)}
                                        style={{ width: "200px" }}
                                        required
                                        placeholder="Enter reference number"
                                    />
                                    <small style={{ color: '#666', fontSize: '12px' }}>
                                    </small>
                                </div>
                            </div>
                            {/* EVERYTHING BELOW REMAINS EXACTLY THE SAME */}
                            <div className="table-section">
                                <div className="table-header">
                                    <span>Items</span>
                                    <button
                                        type="button"
                                        className="settings-btn"
                                        onClick={() => alert("Settings not implemented yet")}
                                    >
                                        <FaCog />
                                    </button>
                                </div>
                                <div className="table-container">
                                    <table className="item-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Category</th>
                                                <th>Description</th>
                                                <th>Amount</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(tableData) && tableData.length > 0 ? (
                                                tableData.map((row) => (
                                                    <tr key={row?.id || Math.random()}>
                                                        <td>{row?.rowNumber || '-'}</td>
                                                        <td>
                                                            <select
                                                                value={row?.category || "General"}
                                                                onChange={(e) => {
                                                                    handleUpdateRow(row?.id, "category", e.target.value);
                                                                    if (row?.rowNumber === "1") {
                                                                        setCategory(e.target.value);
                                                                    }
                                                                }}
                                                            >
                                                                {categories.length === 0 ? (
                                                                    <option disabled>No categories found</option>
                                                                ) : (
                                                                    categories.map((cat) => (
                                                                        <option key={cat.name} value={cat.name}>
                                                                            {cat.display_name}
                                                                        </option>
                                                                    ))
                                                                )}
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                value={row?.description || ""}
                                                                onChange={(e) => handleUpdateRow(row?.id, "description", e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={row?.totalAmount ?? ""}
                                                                onChange={(e) => {
                                                                    const value = e.target.value === "" ? "" : Math.max(0, parseFloat(e.target.value)).toFixed(2);
                                                                    handleUpdateRow(row?.id, "totalAmount", value);
                                                                }}
                                                                placeholder="0.00"
                                                            />
                                                        </td>
                                                        <td className="action-buttons">
                                                            <button
                                                                type="button"
                                                                className="icon-button copy"
                                                                onClick={() => handleCopyRow(row?.id)}
                                                                title="Copy"
                                                            >
                                                                <FaCopy />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="icon-button delete"
                                                                onClick={() => handleDeleteRow(row?.id)}
                                                                title="Delete"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="no-items">No items available</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <td colSpan="5"></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ display: "flex", gap: "1rem", marginTop: "-10px", alignItems: "flex-start" }}>
                                    <button
                                        type="button"
                                        className="add-button"
                                        onClick={handleAddRow}
                                        title="Add Row"
                                    >
                                        Add Lines
                                    </button>
                                    <button
                                        type="button"
                                        className="add-secondary-button"
                                        onClick={handleClearAllLines}
                                        title="Clear Lines"
                                    >
                                        Clear all Lines
                                    </button>
                                </div>
                                <div style={{ display: "flex", alignItems: "right", marginLeft: "1280px", marginTop: "-50px" }}>
                                    <label style={{ fontSize: "12px", fontWeight: "bold" }}>Total Amount</label>
                                    <p style={{ fontWeight: "bold", color: "#333", fontSize: "12px" }}>
                                        : £{totalAmount ? parseFloat(totalAmount).toFixed(2) : "0.00"}
                                    </p>
                                </div>
                                <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", alignItems: "flex-start" }}>
                                    <div className="form-group" style={{ flex: 1, marginBottom: "-50px", marginTop: "30px" }}>
                                        <label>Memo/Description</label>
                                        <textarea
                                            value={description || ""}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Enter memo or description"
                                            style={{
                                                width: "100%",
                                                height: "100px",
                                                padding: "0.7rem",
                                                border: "1px solid #ddd",
                                                borderRadius: "4px",
                                                resize: "vertical",
                                            }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginTop: "1rem" }}>
                                        <label>Expense PDF (Optional)</label>
                                        <div
                                            className="upload-area"
                                            style={{
                                                padding: "0.5rem",
                                                height: "100px",
                                                marginTop: "15px",
                                                width: "100%",
                                                textAlign: "center",
                                                cursor: "pointer",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                border: "1px solid #ddd",
                                                borderRadius: "4px",
                                            }}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <FaCloud size={20} style={{ marginBottom: "0.5rem", color: "#808080" }} />
                                            <p style={{ margin: "0", fontSize: "0.9rem" }}>
                                                Click to select PDF file
                                            </p>
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                accept=".pdf"
                                                onChange={handleFileChange}
                                                style={{ display: "none" }}
                                            />
                                            {expenseFile && (
                                                <p
                                                    style={{
                                                        margin: "0.3rem 0",
                                                        color: "#333",
                                                        fontSize: "0.8rem",
                                                    }}
                                                >
                                                    Selected: {expenseFile.name}
                                                </p>
                                            )}
                                            {!expenseFile && (
                                                <p
                                                    style={{
                                                        margin: "0.3rem 0",
                                                        color: "#666",
                                                        fontSize: "0.8rem",
                                                    }}
                                                >
                                                    No file selected
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ textAlign: "center", marginTop: "1rem" }}>
                                    <button
                                        type="button"
                                        style={{
                                            padding: "0.7rem 1.5rem",
                                            backgroundColor: "transparent",
                                            color: "#004aad",
                                            fontWeight: "bold",
                                            marginTop: "-10px",
                                            marginBottom: "-20px",
                                        }}
                                        onClick={() => alert("Privacy settings applied!")}
                                    >
                                        Privacy
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div
                            className="form-action-buttons"
                            style={{
                                padding: "1rem",
                                backgroundColor: "#fff",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderTop: "1px solid #ddd",
                                marginTop: "auto",
                            }}
                        >
                            <div>
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setShowCreateExpensePopup(false)}
                                    style={{ marginRight: "1rem" }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="clear-btn"
                                    onClick={handleClearLocal}
                                    style={{ marginRight: "0" }}
                                >
                                    Clear
                                </button>
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="print-btn"
                                    onClick={handlePrint}
                                    style={{ marginRight: "1rem", borderRadius: "3px" }}
                                >
                                    Print
                                </button>
                                <button
                                    type="button"
                                    className="recurring-btn"
                                    onClick={handleRecurring}
                                    style={{ marginRight: "0" }}
                                >
                                    Recurring
                                </button>
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    className="submit-btn"
                                    style={{ marginRight: "1rem" }}
                                >
                                    {selectedExpense ? "Update" : "Save"}
                                </button>
                                <button
                                    type="button"
                                    className="save-new-btn"
                                    onClick={handleSaveAndNewClick}
                                    style={{ marginRight: "0" }}
                                >
                                    Save and New
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        )
    );
};

export default SupplierExpensePopup;